import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import { InputTypes, SpinnerTypes, IconTypes } from 'constants/Constants';
import { isObjectWithContent } from 'utils/objects';

import IconButton from 'components/common/IconButton/IconButton';
import Icon from 'components/common/Icon/Icon';
import SolidSpinner from 'components/loading/SolidSpinner/SolidSpinner';
import { Label } from 'components/layout/Typography/Typography';

import styles from './Input.module.scss';

const s = classNames.bind(styles);

const Input = ({
	autoComplete,
	autoFocus,
	characterLimit,
	inputRef,
	className,
	disabled,
	error,
	iconClassName,
	inputClassName,
	iconRight,
	iconLeft,
	label,
	marginBottom,
	name,
	onBlur,
	onChange,
	onEnter,
	onFocus,
	placeholder,
	size,
	type,
	helperText,
	required,
	value,
	isFirefox,
	min,
	max
}) => {
	const [isFocused, setFocus] = useState(false);

	/**
	|--------------------------------------------------
	| @function onInputBlur
	|--------------------------------------------------
	*/
	const onInputBlur = event => {
		if (onBlur) {
			onBlur(event);
		}
		setFocus(false);
	};

	/**
	|--------------------------------------------------
	| @function onInputFocus
	|--------------------------------------------------
	*/
	const onInputFocus = event => {
		if (onFocus) {
			onFocus(event);
		}
		setFocus(true);
	};

	/**
	|--------------------------------------------------
	| @function getCharactersLeft
	|--------------------------------------------------
	 */
	const getCharactersLeft = () => characterLimit - value.length;

	/**
	|--------------------------------------------------
	| @function onKeyPressed
	| used to handle enter press
	|--------------------------------------------------
	*/
	const onKeyPressed = event => {
		if (onEnter) {
			if (event.key === 'Enter') {
				onEnter(event);
			}
		}
	};

	/**
	|--------------------------------------------------
	| @function getIconElement
	|--------------------------------------------------
	*/
	const getIconElement = iconObject => (
		<React.Fragment>
			{iconObject.loading && <SolidSpinner className={s('spinner')} color={SpinnerTypes.BLUE} size={SpinnerTypes.SMALL} />}
			{iconObject.onClickCallback && (
				<IconButton
					className={s({ hideIconButton: iconObject.loading })}
					iconClassName={s('icon', {
						icon_focused: isFocused,
						icon_error: error,
						icon_displayNone: iconObject.loading,
						[iconClassName]: iconClassName
					})}
					disabled={iconObject.disabled}
					icon={iconObject.icon}
					onClickCallback={iconObject.onClickCallback}
				/>
			)}
		</React.Fragment>
	);

	/**
	|--------------------------------------------------
	| @function render() markup
	|--------------------------------------------------
	*/
	return (
		<div
			className={s('container', {
				container_marginBottom: marginBottom,
				container_marginBottomLarge: marginBottom && size === InputTypes.LARGE,
				[className]: className
			})}
		>
			{label && (
				<Label htmlFor={name} className={s('flex')} disabled={disabled}>
					{`${label}${required ? '*' : ''}`}
					{characterLimit && (
						<span className={s('characterLimit', { characterLimit_error: Math.sign(getCharactersLeft()) < 1 })}>
							{`${getCharactersLeft()} characters`}
						</span>
					)}
				</Label>
			)}
			<div className={s('innerContainer')}>
				{isObjectWithContent(iconLeft) && <div className={s('leftIconElement')}>{getIconElement(iconLeft)}</div>}
				<input
					ref={inputRef}
					className={s('input', {
						input_small: size === InputTypes.SMALL,
						input_medium: size === InputTypes.MEDIUM,
						input_large: size === InputTypes.LARGE,
						input_hasError: error && error.length > 0,
						input_hasIconLeft: iconLeft,
						input_hasIconRight: iconRight,
						input_disabled: disabled,
						[inputClassName]: inputClassName
					})}
					autoFocus={autoFocus}
					type={type}
					name={name}
					autoComplete={autoComplete}
					placeholder={placeholder}
					value={value}
					onChange={onChange}
					onKeyPress={onKeyPressed}
					onBlur={onInputBlur}
					onFocus={onInputFocus}
					disabled={disabled}
					required={required}
					min={min}
					max={max}
					pattern={type === 'time' ? '[0-9]{2}:[0-9]{2}' : ''} // IE11 does not support type="time" so a simple validation is required.
				/>
				{isObjectWithContent(iconRight) && <div className={s('rightIconElement')}>{getIconElement(iconRight)}</div>}
				{type === 'time' && !isFirefox && (
					<div className={s('rightIconElement', { pointerEventsNone: true })}>
						<Icon className={s('timeIcon')} icon={IconTypes.CALENDAR} />
					</div>
				)}
				{type === 'date' && !isFirefox && (
					<div className={s('rightIconElement', { pointerEventsNone: true })}>
						<Icon className={s('dateIcon')} icon={IconTypes.DATE_RANGE} />
					</div>
				)}
			</div>
			{helperText && <p className={s('helperText')}>{helperText}</p>}
			{error && <p className={s('error')}>{error}</p>}
		</div>
	);
};

Input.defaultProps = {
	size: InputTypes.MEDIUM,
	type: 'text',
	autoFocus: false,
	marginBottom: true,
	placeholder: null
};

Input.propTypes = {
	autoComplete: PropTypes.string,
	autoFocus: PropTypes.bool,
	className: PropTypes.string,
	disabled: PropTypes.bool,
	error: PropTypes.string,
	iconClassName: PropTypes.string,
	inputClassName: PropTypes.string,
	label: PropTypes.string,
	required: PropTypes.bool,
	marginBottom: PropTypes.bool,
	name: PropTypes.string.isRequired,
	onBlur: PropTypes.func,
	onChange: PropTypes.func.isRequired,
	onClear: PropTypes.func, // Function to use to clear the field
	onEnter: PropTypes.func,
	onFocus: PropTypes.func,
	placeholder: PropTypes.string,
	inputRef: PropTypes.object,
	size: PropTypes.string,
	min: PropTypes.number,
	max: PropTypes.number,
	type: PropTypes.oneOf(['text', 'number', 'password', 'time', 'email', 'date']),
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	helperText: PropTypes.string,
	characterLimit: PropTypes.number,
	iconRight: PropTypes.shape({
		loading: PropTypes.bool,
		icon: PropTypes.string,
		onClickCallback: PropTypes.func
	}),
	iconLeft: PropTypes.shape({
		loading: PropTypes.bool,
		icon: PropTypes.string,
		onClickCallback: PropTypes.func
	}),
	isFirefox: PropTypes.bool
};

export default Input;
